import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import productLogo from "../assets/images/country-seal.jpg";
import mlgLogo from "../assets/images/mlg-logo.png";
import { BASE_URL, RECAPTCHA_SITE_KEY } from "../config/api";
import { showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => number;
      reset: (widgetId?: number) => void;
      getResponse: (widgetId?: number) => string;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = "google-recaptcha-script";

const ensureRecaptchaScript = () => {
  if (document.getElementById(RECAPTCHA_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = RECAPTCHA_SCRIPT_ID;
  script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

const whenRecaptchaReady = (): Promise<void> =>
  new Promise((resolve, reject) => {
    ensureRecaptchaScript();
    const start = Date.now();
    const timer = window.setInterval(() => {
      if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
        window.clearInterval(timer);
        resolve();
      } else if (Date.now() - start > 15000) {
        window.clearInterval(timer);
        reject(new Error("reCAPTCHA failed to load (check network / domain)."));
      }
    }, 100);
  });

const Register = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (submitted) return;
    let cancelled = false;
    whenRecaptchaReady()
      .then(() => {
        if (
          cancelled ||
          !captchaRef.current ||
          !window.grecaptcha ||
          widgetIdRef.current !== null
        ) {
          return;
        }
        captchaRef.current.innerHTML = "";
        try {
          widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: (token: string) => setCaptchaToken(token),
            "expired-callback": () => setCaptchaToken(""),
            "error-callback": () => setCaptchaToken(""),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("reCAPTCHA render failed", err);
        }
      })
      // eslint-disable-next-line no-console
      .catch((err) => console.error(err));
    return () => {
      cancelled = true;
    };
  }, [submitted]);

  const resetCaptcha = () => {
    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
    setCaptchaToken("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!captchaToken) {
      showErrorToast("Please complete the CAPTCHA challenge.");
      return;
    }
    setIsSubmitting(true);
    // Backend builds "{baseUrl}/create-password?token=..."; the CreatePassword page
    // is served at /reset-password/create-password.
    const baseUrl = `${window.location.origin}/reset-password`;
    try {
      const response = await fetch(`${BASE_URL}/Auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          primaryPhone: phone.trim(),
          baseUrl,
          captchaToken,
        }),
      });

      if (!response.ok) {
        let message = GENERIC_ERROR_MESSAGE;
        try {
          const err = (await response.json()) as { message?: string };
          if (err?.message) message = err.message;
        } catch {
          /* ignore */
        }
        resetCaptcha();
        throw new Error(message);
      }

      setSubmitted(true);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : GENERIC_ERROR_MESSAGE;
      showErrorToast(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-body">
      <section className="auth-shell">
        <div className="auth-hero">
          <div className="hero-overlay" />
          <div className="hero-content">
            <div className="hero-logo">
              <img src={mlgLogo} alt="Ministry of Local Government" />
            </div>
            <h1>Ministry of Local Government</h1>
            <br />
            <div className="hero-footnote">Self-Service Registration</div>
          </div>
        </div>
        <div className="auth-panel">
          <div className="product-logo">
            <img className="logo-desktop" src={productLogo} alt="Seal" />
            <img
              className="logo-mobile"
              src={mlgLogo}
              alt="Ministry of Local Government"
            />
          </div>
          <div className="auth-panel-main">
            <span className="auth-description">
              Register for a Marriage &amp; Divorce Certificate
            </span>

            {submitted ? (
              <div className="forgot-success" style={{ marginTop: "1rem" }}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M20 6 9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>
                  Registration successful. Check your email for a link to set
                  your password, then log in to start your application.
                </span>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleSubmit}>
                <div className="form-field">
                  <input
                    id="firstName"
                    type="text"
                    placeholder=" "
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <label htmlFor="firstName">First Name</label>
                </div>
                <div className="form-field">
                  <input
                    id="lastName"
                    type="text"
                    placeholder=" "
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                  <label htmlFor="lastName">Last Name</label>
                </div>
                <div className="form-field">
                  <input
                    id="email"
                    type="email"
                    placeholder=" "
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <label htmlFor="email">Email Address</label>
                </div>
                <div className="form-field">
                  <input
                    id="phone"
                    type="tel"
                    placeholder=" "
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                  <label htmlFor="phone">Phone Number</label>
                </div>

                <div className="captcha-field" ref={captchaRef} />

                <button
                  className="primary-btn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Registering..." : "Register"}
                </button>
              </form>
            )}

            <button
              type="button"
              className="register-link"
              onClick={() => navigate("/")}
            >
              Back to Sign In
            </button>
          </div>
          <div className="login-powered">
            Powered by{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.mwetana.com.lr"
            >
              MWETANA
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Register;
